import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { color, compose, layout, space, typography } from 'styled-system';

import { defaultExcludedProps, forwardValidProp } from '../../utils/style';

const mobileSizeMap = {
  size1: 'size3',
  size2: 'size4',
  size3: 'size5',
  size4: 'size6',
  size5: 'size7',
  size6: 'size8',
};

const baseStyles = ({ theme, lineBreak, wordBreak, whiteSpace }) => css`
  color: ${theme.colors.heading.foreground};
  font-weight: ${theme.typography.fontWeights.regular};
  line-break: ${lineBreak};
  word-break: ${wordBreak};
  white-space: ${whiteSpace};
`;

const headingStyles = ({ theme, size }) => {
  const headingsStylesMap = {
    size1: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size1].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      letter-spacing: ${theme.typography.headings[mobileSizeMap.size1]
        .letterSpacing};
      line-height: ${theme.typography.headings[mobileSizeMap.size1].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size1.fontSize};
        font-weight: ${theme.typography.fontWeights.black};
        letter-spacing: ${theme.typography.headings.size1.letterSpacing};
        line-height: ${theme.typography.headings.size1.lineHeight};
      }
    `,
    size2: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size2].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      letter-spacing: ${theme.typography.headings[mobileSizeMap.size2]
        .letterSpacing};
      line-height: ${theme.typography.headings[mobileSizeMap.size2].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size2.fontSize};
        font-weight: ${theme.typography.fontWeights.bold};
        letter-spacing: ${theme.typography.headings.size2.letterSpacing};
        line-height: ${theme.typography.headings.size2.lineHeight};
      }
    `,
    size3: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size3].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      letter-spacing: ${theme.typography.headings[mobileSizeMap.size3]
        .letterSpacing};
      line-height: ${theme.typography.headings[mobileSizeMap.size3].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size3.fontSize};
        font-weight: ${theme.typography.fontWeights.bold};
        letter-spacing: ${theme.typography.headings.size3.letterSpacing};
        line-height: ${theme.typography.headings.size3.lineHeight};
      }
    `,
    size4: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size4].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      line-height: ${theme.typography.headings[mobileSizeMap.size4].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size4.fontSize};
        font-weight: ${theme.typography.fontWeights.bold};
        line-height: ${theme.typography.headings.size4.lineHeight};
      }
    `,
    size5: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size5].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      line-height: ${theme.typography.headings[mobileSizeMap.size5].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size5.fontSize};
        font-weight: ${theme.typography.fontWeights.bold};
        line-height: ${theme.typography.headings.size5.lineHeight};
      }
    `,
    size6: css`
      font-size: ${theme.typography.headings[mobileSizeMap.size6].fontSize};
      font-weight: ${theme.typography.fontWeights.bold};
      line-height: ${theme.typography.headings[mobileSizeMap.size6].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.headings.size6.fontSize};
        font-weight: ${theme.typography.fontWeights.bold};
        line-height: ${theme.typography.headings.size6.lineHeight};
      }
    `,
  };

  return headingsStylesMap[size];
};

const StyledHeading = styled('h1', {
  shouldForwardProp: (prop) =>
    forwardValidProp(prop, [...defaultExcludedProps, 'fontSize']),
})`
  ${baseStyles}
  ${headingStyles}
  ${compose(color, layout, space, typography)}
`;

const getMarkupFromSizeProp = (size) => {
  const headingNumber = Number(size.substring(size.length - 1, size.length));

  if (headingNumber < 0 && headingNumber > 6) {
    return null;
  }

  return `h${headingNumber}`;
};

const Heading = forwardRef(({ as, size, ...rest }, ref) => (
  <StyledHeading
    as={as || getMarkupFromSizeProp(size)}
    size={size}
    ref={ref}
    {...rest}
  />
));

Heading.HTML_MARKUP_OPTIONS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'span',
  'a',
  'p',
];
Heading.SIZES = ['size1', 'size2', 'size3', 'size4', 'size5', 'size6'];
Heading.displayName = 'Heading';

Heading.defaultProps = {
  as: null,
  size: 'size1',
  lineBreak: null,
  wordBreak: null,
  whiteSpace: null,
};

Heading.propTypes = {
  /**
   * HTML markup to be rendered
   */
  as: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'p']),
  /**
   * Heading to be rendered inside component
   */
  children: PropTypes.node.isRequired,
  /**
   * `line-break` css prop
   */
  lineBreak: PropTypes.string,
  /**
   * Renders Heading in one of the following sizes visually and semantically.
   *
   * Can be semantically overwritten by passing `as` prop
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3', 'size4', 'size5', 'size6']),
  /**
   * `word-break` css prop
   */
  wordBreak: PropTypes.string,
  /**
   * `white-space` css prop
   */
  whiteSpace: PropTypes.string,
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [typography](https://styled-system.com/api#typography) utility
   */
  ...propTypes.typography,
};

export default Heading;
